import { resumeAudioContext } from './resumeAudioContext.js';

/**
 * Gapless PCM audio player. Backend streams raw PCM 24kHz mono int16 LE
 * (ElevenLabs `pcm_24000`); each chunk arrives as a binary WS frame and is
 * decoded straight into an AudioBuffer + scheduled at the cursor.
 *
 * Why not <audio> + Blob: the backend can't deliver a complete container
 * (no header is written) and we want sub-100ms time-to-first-audio. Web
 * Audio scheduling is the natural fit for chunked PCM.
 */

const SAMPLE_RATE = 24_000;

/**
 * Tope de audio encolado por delante (segundos) mientras el AudioContext está
 * suspendido (pausa). Con el reloj congelado las sources ya scheduleadas no
 * disparan `onended`, así que `active` sólo crece y retiene AudioBuffers de PCM.
 * En una pausa normal el server corta el TTS (`session.pause`) en cientos de ms
 * → la cola queda en pocos segundos y esto NUNCA se dispara; es una red de
 * seguridad para que `active` no crezca sin tope si algo sigue streameando
 * durante una pausa muy larga.
 */
const MAX_SUSPENDED_QUEUE_SEC = 60;

export interface AudioPlayerOptions {
  /** Override the AudioContext sample rate. Default 24000. */
  sampleRate?: number;
}

/**
 * AudioContext de la sesión de voz VIVA (la que reproduce al coach), o `null`
 * si todavía no hubo `unlock()` o ya se destruyó. Singleton de módulo, mismo
 * criterio que `getSharedMicStream()` en `MicCapture`.
 *
 * Existe por el ruteo de audio del OS, no por comodidad: en Android, encender
 * el mic con `echoCancellation` pone al dispositivo en modo comunicación, y
 * **cada salida de audio se queda con el canal que había cuando se abrió**.
 * Este contexto se crea ANTES del mic (`connection.ts`: `player.unlock()` y
 * recién después `MicCapture`), así que queda en el canal de MULTIMEDIA. Un
 * `<audio>` creado más tarde — el play de una burbuja, mitad de charla — nace
 * con el mic ya encendido y se engancha al canal de LLAMADAS: misma voz, otra
 * perilla de volumen, y en iOS hasta puede salir por el auricular. La única
 * forma de que suenen igual es que salgan por la MISMA salida, así que quien
 * reproduzca un clip enruta su `<audio>` por acá (`createMediaElementSource`).
 *
 * Devuelve el contexto sólo si está `running`: suspendido (charla en pausa)
 * enrutar por él sería silencio, y el caller tiene que quedarse con su
 * `<audio>` suelto.
 */
let liveOutputCtx: AudioContext | null = null;

export function getLiveOutputContext(): AudioContext | null {
  return liveOutputCtx && liveOutputCtx.state === 'running' ? liveOutputCtx : null;
}

export class AudioPlayer {
  private ctx: AudioContext | null = null;
  private cursor = 0;
  private active = new Set<AudioBufferSourceNode>();
  private muted = false;
  private gain: GainNode | null = null;
  private analyser: AnalyserNode | null = null;
  /** Buffer reusable para `analyser.getFloatTimeDomainData` — evita alocar por frame. */
  private analyserBuf: Float32Array<ArrayBuffer> | null = null;
  private volume = 1;
  private readonly sampleRate: number;
  /**
   * Audio del turno del coach en curso, chunk por chunk (inicio en el reloj del
   * contexto y duración, s): de acá sale cuánto SONÓ cuando lo cortan, para
   * dejar en el historial sólo lo oído. Lo reinicia `markTurnStart`.
   */
  private turnChunks: Array<{ start: number; dur: number }> = [];
  /**
   * ¿El audio que entra es del turno en curso? Lo cierra `closeTurnAccounting`
   * (el `turn-end`: el server ya mandó todo el audio del turno), así lo que
   * llega después — la coda hablada de correcciones, que no pasa por
   * `thinking` — no se suma al reply.
   */
  private turnOpen = false;

  constructor(opts: AudioPlayerOptions = {}) {
    this.sampleRate = opts.sampleRate ?? SAMPLE_RATE;
  }

  /**
   * Resume / create the AudioContext on first user gesture. Required for
   * autoplay-restricted browsers (Safari, Chrome). Safe to call repeatedly.
   */
  async unlock(): Promise<void> {
    let ctx = this.ctx;
    if (!ctx) {
      const Ctor =
        (globalThis as { AudioContext?: typeof AudioContext }).AudioContext ??
        (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) throw new Error('AudioContext not supported');
      ctx = new Ctor({ sampleRate: this.sampleRate, latencyHint: 'interactive' });
      const gain = ctx.createGain();
      gain.gain.value = this.muted ? 0 : this.volume;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.5;
      gain.connect(analyser);
      analyser.connect(ctx.destination);
      this.ctx = ctx;
      this.gain = gain;
      this.analyser = analyser;
      // Construimos desde un ArrayBuffer explícito — `new Float32Array(N)`
      // se tipa como `Float32Array<ArrayBufferLike>` y `getFloatTimeDomainData`
      // exige `<ArrayBuffer>` bajo `strict`.
      this.analyserBuf = new Float32Array(
        new ArrayBuffer(analyser.fftSize * Float32Array.BYTES_PER_ELEMENT),
      );
      // Publicamos la salida ANTES de que arranque el mic para que los clips
      // puedan enrutarse por el mismo canal del OS. Ver `getLiveOutputContext`.
      liveOutputCtx = ctx;
    }
    // Acotado: con autoplay bloqueado (sin gesto previo) el `resume()` queda
    // pendiente para siempre y colgaba el arranque ANTES de abrir el WS. Ver
    // `resumeAudioContext`.
    await resumeAudioContext(ctx, 'player');
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.gain) this.gain.gain.value = muted ? 0 : this.volume;
  }

  /** Empieza un turno del coach: el primer chunk de su audio está por llegar. */
  markTurnStart(): void {
    this.turnChunks = [];
    this.turnOpen = true;
  }

  /** Llegó el `turn-end`: el audio que siga no es de este turno. */
  closeTurnAccounting(): void {
    this.turnOpen = false;
  }

  /**
   * Audio del turno en curso que llegó a sonar y que se recibió (ms de
   * contenido: un hueco por red lenta no cuenta). Con el contexto suspendido el
   * reloj no avanza, así que en una pausa queda fijo.
   */
  getTurnPlayback(): { playedMs: number; receivedMs: number } {
    const now = this.ctx?.currentTime ?? 0;
    let played = 0;
    let received = 0;
    for (const c of this.turnChunks) {
      received += c.dur;
      played += Math.min(c.dur, Math.max(0, now - c.start));
    }
    return { playedMs: Math.round(played * 1000), receivedMs: Math.round(received * 1000) };
  }

  isMuted(): boolean {
    return this.muted;
  }

  /** Set output volume (0..1). Persistido por separado del flag de mute. */
  setVolume(volume: number): void {
    const clamped = Math.min(1, Math.max(0, volume));
    this.volume = clamped;
    if (this.gain && !this.muted) this.gain.gain.value = clamped;
  }

  getVolume(): number {
    return this.volume;
  }

  /**
   * Append a chunk of PCM 16-bit signed little-endian audio. Schedules it at
   * the running playback cursor so consecutive chunks play back-to-back without
   * gaps. Caller is expected to call {@link unlock} once before pushing.
   */
  push(chunk: ArrayBuffer | Uint8Array): void {
    if (!this.ctx || !this.gain) return;
    const bytes = chunk instanceof Uint8Array ? chunk : new Uint8Array(chunk);
    if (bytes.byteLength < 2) return;

    // Red de seguridad anti-acumulación durante un suspend largo: con el reloj
    // congelado las sources ya scheduleadas no disparan `onended`, así que
    // `active` sólo crece. Si la cola por delante ya es enorme, descartamos el
    // chunk en vez de retener otro AudioBuffer. No afecta una pausa normal (la
    // cola queda en pocos segundos). Ver `MAX_SUSPENDED_QUEUE_SEC`.
    if (
      this.ctx.state === 'suspended' &&
      this.cursor - this.ctx.currentTime > MAX_SUSPENDED_QUEUE_SEC
    ) {
      return;
    }

    const samples = bytes.byteLength / 2;
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const out = buffer.getChannelData(0);
    for (let i = 0; i < samples; i++) {
      const s = view.getInt16(i * 2, true);
      out[i] = s < 0 ? s / 0x8000 : s / 0x7fff;
    }

    const now = this.ctx.currentTime;
    const startAt = Math.max(now, this.cursor);
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(this.gain);
    src.onended = (): void => {
      this.active.delete(src);
    };
    src.start(startAt);
    this.active.add(src);
    this.cursor = startAt + buffer.duration;
    if (this.turnOpen) this.turnChunks.push({ start: startAt, dur: buffer.duration });
  }

  /**
   * RMS [0..1] de la onda actualmente sonando por el output. Calculado del
   * AnalyserNode que se intercala entre el gain y `ctx.destination`. Refleja
   * el volumen acústico real (post-gain, post-mute) — si el usuario bajó el
   * volumen a 0 o muteó, devuelve ~0. Usado por la orb para animar el
   * "habla la AI" en proporción al audio efectivo, no a un timer fijo.
   *
   * Si el contexto aún no se creó (no hubo `unlock()`), devuelve 0.
   */
  getOutputRms(): number {
    const analyser = this.analyser;
    const buf = this.analyserBuf;
    if (!analyser || !buf) return 0;
    analyser.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = buf[i] ?? 0;
      sum += v * v;
    }
    return Math.sqrt(sum / buf.length);
  }

  /**
   * Whether audio is in flight. Useful for barge-in detection.
   */
  isPlaying(): boolean {
    if (!this.ctx) return false;
    return this.cursor > this.ctx.currentTime || this.active.size > 0;
  }

  /**
   * Tiempo actual del AudioContext en milisegundos. Es el reloj que avanza
   * en sync con el audio efectivamente reproducido (NO con wall-clock —
   * `ctx.currentTime` se congela durante un `suspend()` y reanuda exacto).
   * Lo usa el karaoke client-side para anclar timestamps de alignment al
   * scheduling real del audio.
   */
  getClockMs(): number {
    return this.ctx ? this.ctx.currentTime * 1000 : 0;
  }

  /**
   * Instante del reloj del AudioContext (ms) en el que TERMINA lo último ya
   * scheduleado — el cursor de reproducción. Es el fin acústico exacto del
   * audio encolado, sin la imprecisión del poll de 50 ms de {@link awaitIdle}.
   *
   * Sólo para instrumentación (el panel de medición en device lo usa como t0
   * de referencia): el camino de producción se ancla a `awaitIdle`, que es lo
   * que efectivamente arma la ventana de settle.
   */
  getScheduledEndMs(): number {
    return this.ctx ? this.cursor * 1000 : 0;
  }

  /**
   * Lectura APAREADA del reloj del AudioContext de salida y de
   * `performance.now()`, más las latencias que el browser declara. Puente
   * para cruzar el reloj del player con el del mic (que es OTRO AudioContext).
   * `null` si todavía no hubo `unlock()`.
   */
  getAudioTimestamp(): {
    contextTime: number;
    performanceTime: number;
    sampleRate: number;
    baseLatency: number | null;
    outputLatency: number | null;
  } | null {
    const ctx = this.ctx;
    if (!ctx) return null;
    const contextTime = ctx.currentTime;
    const performanceTime = performance.now();
    const withLatency = ctx as AudioContext & { outputLatency?: number };
    return {
      contextTime,
      performanceTime,
      sampleRate: ctx.sampleRate,
      baseLatency: typeof ctx.baseLatency === 'number' ? ctx.baseLatency : null,
      outputLatency:
        typeof withLatency.outputLatency === 'number' ? withLatency.outputLatency : null,
    };
  }

  /**
   * Resuelve cuando termina la reproducción de todo lo ya scheduled (queue
   * vacía y cursor pasado). El caller espera a que el bot termine de
   * "hablar por los parlantes" antes de notificar al backend que el
   * playback finalizó (mensaje WS `playback-finished`).
   *
   * Idempotente: si no hay audio en cola resuelve inmediato. Garantiza un
   * mínimo de tiempo para evitar resoluciones falsamente tempranas cuando
   * el último chunk recién está siendo decodificado (poll cada 50ms).
   */
  awaitIdle(): Promise<void> {
    return new Promise<void>((resolve) => {
      const ctx = this.ctx;
      if (!ctx) {
        resolve();
        return;
      }
      const check = (): void => {
        if (!this.isPlaying()) {
          resolve();
          return;
        }
        // Mientras el contexto está suspendido (pausa) el reloj no avanza, así
        // que `isPlaying()` se mantiene true y no hay nada que drenar: poll más
        // espaciado para no despertar cada 50ms durante toda la pausa. Al
        // `resume()` (o `reset()`/`destroy()`, que adelantan el cursor) el
        // siguiente poll resuelve normalmente.
        const delay = ctx.state === 'suspended' ? 250 : 50;
        setTimeout(check, delay);
      };
      check();
    });
  }

  /**
   * Congela el AudioContext: detiene `currentTime`, las sources ya scheduleadas
   * quedan suspendidas en el sample exacto donde estaban, y nuevos chunks
   * que lleguen siguen alineándose al cursor congelado — por lo que se
   * acumulan listos para sonar. Al `resume()`, todo arranca sample-exact
   * desde el punto de congelamiento.
   *
   * Idempotente. No-op si el contexto aún no se construyó.
   */
  async suspend(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    if (ctx.state === 'suspended' || ctx.state === 'closed') return;
    try {
      await ctx.suspend();
    } catch {
      /* ignore */
    }
  }

  /**
   * Reanuda el AudioContext congelado. Si todavía hay chunks encolados de un
   * turno previo, el usuario los escucha sin gaps. Idempotente.
   */
  async resume(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    if (ctx.state === 'running' || ctx.state === 'closed') return;
    try {
      await ctx.resume();
    } catch {
      /* ignore */
    }
  }

  /**
   * Cancel any scheduled audio. Used on InterruptionFrame ack — the assistant
   * reply is being cut short and the user is about to speak again.
   */
  reset(): void {
    for (const src of this.active) {
      try {
        src.stop();
      } catch {
        /* ignore */
      }
      try {
        src.disconnect();
      } catch {
        /* ignore */
      }
    }
    this.active.clear();
    if (this.ctx) {
      this.cursor = this.ctx.currentTime;
      // Lo que no llegó a sonar ya no va a sonar: el turno queda con lo oído.
      const now = this.ctx.currentTime;
      this.turnChunks = this.turnChunks.map((c) => ({
        start: c.start,
        dur: Math.min(c.dur, Math.max(0, now - c.start)),
      }));
    }
  }

  /** Tear down the AudioContext entirely. Use only on PWA unload. */
  async destroy(): Promise<void> {
    this.reset();
    // Sólo si la salida publicada es LA NUESTRA: una sesión nueva pudo haber
    // publicado la suya mientras este destroy iba en camino.
    if (liveOutputCtx === this.ctx) liveOutputCtx = null;
    try {
      await this.ctx?.close();
    } catch {
      /* ignore */
    }
    this.ctx = null;
    this.gain = null;
    this.analyser = null;
    this.analyserBuf = null;
  }
}
